import { Injectable } from "@angular/core";
import { NgEnvVarsService } from "../ng-services/ng-env-vars.service";
import { NgToastrService } from "../ng-components/ng-toastr/ng-toastr.service";
import { Observable } from "rxjs";
import { BASE_CONSTANTS, BASE_URLS, IService, TCI_ENVIRONMENT_VAR_TYPE, mapIServiceToTenantAppType } from "@tci/common";
import { Http } from "@angular/http";
import { NgUserService } from "./ng-user.service";

@Injectable()
export class NgUiDiscovery {
    private static instance: NgUiDiscovery;

    private reqUISvcsObsWithModules: Observable<IService[]>;

    private reqUISvcsObs: Observable<IService[]>;
    private svcsCache: IService[];
    private svcsLastCheck: Date;

    private sysConf;

    public static getInstance(http: Http, envVarService: NgEnvVarsService, toastr: NgToastrService, userService: NgUserService) {
        if (!NgUiDiscovery.instance) {
            NgUiDiscovery.instance = new NgUiDiscovery(http, envVarService, toastr, userService);
        }
        return NgUiDiscovery.instance;
    }

    constructor(
        private http: Http,
        private envVarService: NgEnvVarsService,
        private toastr: NgToastrService,
        private userService: NgUserService
    ) {
        this.sysConf = (<any>SystemJS).getConfig();
    }

    /**
     * Gets Remote Services
     * It shares the request if a previous one has not finished
     * It also shares a temporary cache for calls made in a 2.5s time lapse
     * @param includeLocal
     * @return @Observable<IService[]>
     */
    public getRemoteServices(includeLocal: boolean = false): Observable<IService[]> {

        // Verify if 2.5s have passed since last check
        const checkNeeded = !this.svcsCache || new Date().getTime() - this.svcsLastCheck.getTime() > 2500;

        // Verfiy if 10s have passed since last check
        const checkNeededFull = !this.svcsCache || new Date().getTime() - this.svcsLastCheck.getTime() > 10000;

        if (checkNeeded || checkNeededFull) { // No cache or not 2.5 have passed
            const url = checkNeededFull ? `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.UI_SERVICES}` : `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.UI_SERVICES}?isShort=true`;
            if (!this.reqUISvcsObs) {
                this.reqUISvcsObs = this.getRemoteServicesObservable(includeLocal, url)
                    .map(svcs => {
                        this.svcsLastCheck = new Date();
                        this.svcsCache = svcs;
                        return svcs;
                    })
                    .finally(() => delete this.reqUISvcsObs)
                    .share();
            }

            return this.reqUISvcsObs;
        } else { // Return the temporary cache
            return Observable.of(this.svcsCache);
        }
    }

    /**
     * --For customizations--
     * Gets Remote Services and include the "modules" attribute
     * It shares the request if a previous one has not finished
     * @param includeLocal
     * @return @Observable<IService[]>
     */
    public getRemoteServicesWithModules(includeLocal: boolean = false): Observable<IService[]> {
        const url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.UI_SERVICES}`;
        if (!this.reqUISvcsObsWithModules) {
            this.reqUISvcsObsWithModules = this.getRemoteServicesObservable(includeLocal, url)
                .finally(() => delete this.reqUISvcsObsWithModules)
                .share();
        }

        return this.reqUISvcsObsWithModules;
    }

    private getRemoteServicesObservable(includeLocal: boolean, url: string): Observable<IService[]> {
        return Observable.combineLatest(
            this.envVarService.getEnvVar(TCI_ENVIRONMENT_VAR_TYPE.TCI_DOCKER_DEPLOYMENT),
            this.userService.getUserInfo(false),
            this.http.get(url)
                .map(res => res.json() as IService[])
                .catch(err => [] as IService[]),
            (isDockerDeployment: boolean, userInfo, uiServices: IService[]) => ({ isDockerDeployment, userInfo, uiServices }))
            .map(res => {
                // TODO: use UI_SERVICE_NAMES after perf merge
                // console.log("User Info:", res.userInfo);
                // res.userInfo.orgId = 0; // TEST Code to force orgId=0
                // Note: when orgId is not a number, or orgId is 0 or negative number, it is a invalid org id.
                const isInvalidOrg = isNaN(res.userInfo.orgId) || res.userInfo.orgId < 1;
                if (isInvalidOrg && !res.isDockerDeployment) {
                    console.log("Connect organization id is ", res.userInfo.orgId);
                    this.toastr.warn("There was a problem loading Connect apps. The Connect functionality will not be available.");
                }
                const isValidLAToken = !!res.userInfo.bpm; // check if bpm does exist
                const isUcsEnabled = !!res.userInfo.ucs;

                const uiServices = res.uiServices.filter(svc => (svc.name !== "scribe" || !isInvalidOrg))
                    .filter(svc => (svc.name !== "liveapps" || isValidLAToken))
                    .filter(v => includeLocal || (v.name !== "webserver"))
                    .filter(svc => svc.name !== "ucs" || isUcsEnabled)

                    // If the tenant is not available at SystemConfig map do not return it
                    .filter(v => this.sysConf.map && this.sysConf.map[mapIServiceToTenantAppType(v).getServiceModuleName()]);

                console.log("Tenant Services available:", uiServices);
                return uiServices;
            });
    }
}
